import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { fetchPendingApplications } from '@/features/applications/api';
import { fetchModerationQueue } from '@/features/moderation/api';
import { fetchUsers } from '@/features/people/api';
import { fetchTickets } from '@/features/support/api';
import { Role } from '@/types/domain';

// -----------------------------------------------------------------------------
// Route prefetching.
//
// Every screen is code-split, which keeps the initial bundle small but means a
// click pays for a chunk download before anything renders. On a fast connection
// that is ~80ms; on the hotel wifi a staff member is actually using, it is the
// difference between "instant" and "did I click that?".
//
// Hovering a nav item is a strong signal of intent and precedes the click by
// 150–300ms of mouse travel. Both the CHUNK and its FIRST QUERY are started
// during that window, so by the time the click lands the screen usually has
// both. This is the same insight as the mobile audit's cache work - the fastest
// request is the one already in flight before it was needed.
//
// Cheap and idempotent: dynamic `import()` is memoised by the module registry
// after the first call, and `prefetchQuery` is a no-op when the data is still
// fresh. Hovering the same item ten times costs one download and one request.
// -----------------------------------------------------------------------------

/**
 * Warms the lazy chunk for a route.
 *
 * The specifiers must match `router.tsx` EXACTLY - Vite keys its module graph on
 * the literal string, so a differing path downloads a second copy of the same
 * screen instead of reusing the one already fetched.
 */
const CHUNK_LOADERS: Record<string, () => Promise<unknown>> = {
  '/': () => import('@/features/dashboard/DashboardPage'),
  '/applications': () => import('@/features/applications/ApplicationsPage'),
  '/review-queue': () => import('@/features/moderation/ReviewQueuePage'),
  '/members': () => import('@/features/people/PeoplePage'),
  '/coaches': () => import('@/features/people/CoachesPage'),
  '/staff': () => import('@/features/staff/StaffPage'),
  '/subscriptions': () => import('@/features/commerce/SubscriptionsPage'),
  '/payments': () => import('@/features/commerce/PaymentsPage'),
  '/tickets': () => import('@/features/support/TicketsPage'),
  '/configuration': () => import('@/features/system/ConfigurationPage'),
  '/audit-log': () => import('@/features/system/AuditLogPage'),
};

/**
 * Warms the first query a route issues.
 *
 * Only the list screens are here, and only their DEFAULT filter state - the
 * view a staff member lands on. Prefetching every filter combination would turn
 * a hover into a burst of requests, which is worse than the problem.
 *
 * The dashboard is deliberately absent: `AppLayout` already holds its analytics
 * query for the sidebar badge counts, so it is warm from the moment the shell
 * mounts and prefetching it again would be a duplicate.
 */
const QUERY_WARMERS: Record<string, (client: QueryClient) => Promise<unknown>> = {
  '/applications': (client) =>
    client.prefetchInfiniteQuery({
      queryKey: queryKeys.applications.list({ q: '' }),
      queryFn: ({ signal }) => fetchPendingApplications({}, signal),
      initialPageParam: undefined,
    }),

  '/review-queue': (client) =>
    client.prefetchInfiniteQuery({
      queryKey: queryKeys.moderation.list({ type: '', status: 'pending' }),
      queryFn: ({ signal }) => fetchModerationQueue({ status: 'pending' }, signal),
      initialPageParam: undefined,
    }),

  '/members': (client) =>
    client.prefetchInfiniteQuery({
      queryKey: queryKeys.users.list({ role: Role.USER, status: '', q: '' }),
      queryFn: ({ signal }) => fetchUsers({ role: Role.USER }, signal),
      initialPageParam: undefined,
    }),

  '/coaches': (client) =>
    client.prefetchInfiniteQuery({
      queryKey: queryKeys.users.list({ role: Role.TRAINER, status: '', q: '' }),
      queryFn: ({ signal }) => fetchUsers({ role: Role.TRAINER }, signal),
      initialPageParam: undefined,
    }),

  '/tickets': (client) =>
    client.prefetchInfiniteQuery({
      queryKey: queryKeys.tickets.list({ status: '' }),
      queryFn: ({ signal }) => fetchTickets({}, signal),
      initialPageParam: undefined,
    }),
};

/** Tracks what has already been warmed, so a hover-jitter is not a request storm. */
const warmed = new Set<string>();

export function prefetchRoute(path: string, client: QueryClient): void {
  void CHUNK_LOADERS[path]?.().catch(() => {
    // A failed prefetch is not an error the user should ever see - the click
    // will retry it through the normal lazy path and surface a real failure
    // there if it persists.
  });

  if (warmed.has(path)) return;
  warmed.add(path);

  void QUERY_WARMERS[path]?.(client).catch(() => {
    // Same: a warm-up that fails costs nothing. The screen's own query runs on
    // mount and owns the error state.
    warmed.delete(path);
  });
}
